import { GridColDef } from '@mui/x-data-grid';
import moment from 'moment';

import { ExpenseTypeBudget } from 'openapi';

import {
  COLUMNS_DEFAULT_OPTIONS,
  DATE_FORMATS,
  EXPENSE_TYPE_NAME
} from 'utils/constants/constants';
import { TranslateFunction } from 'utils/interfaces/Translations';

import { formatNumberWithSpaces, processMoneyInput } from './moneyHelper';

export const updateBudgetCell = ({
  row,
  value,
  month
}: {
  row: ExpenseTypeBudget;
  value: string;
  month: number;
}) => {
  const existingBudget = row.budgets.find((budget) => budget.month === month);
  const previousAmount = existingBudget?.amount ?? 0;
  const validatedAndParsedValue = processMoneyInput(
    value,
    previousAmount.toString()
  );

  if (!existingBudget) {
    return row;
  }

  return {
    ...row,
    budgets: row.budgets.map((budget) =>
      budget.month === month
        ? { ...budget, amount: Number(validatedAndParsedValue) }
        : budget
    )
  };
};

export const getBudgetingColumns = (
  translate: TranslateFunction
): GridColDef<ExpenseTypeBudget>[] => {
  const months = new Array(12).fill(null).map((_, index) => {
    const currentMonth = index + 1;
    return {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: currentMonth.toString(),
      editable: true,
      filterable: false,
      type: 'string',
      align: 'right',
      headerAlign: 'right',
      headerName: moment().month(index).format(DATE_FORMATS.shortMonthFormat),
      valueGetter: (_value: unknown, row: ExpenseTypeBudget) => {
        const budget = row.budgets.find((item) => item.month === currentMonth);
        return budget?.amount ?? 0;
      },
      renderCell: ({ value }: { value: number }) => (
        <>{formatNumberWithSpaces(value)}</>
      ),
      valueSetter: (value: string, row: ExpenseTypeBudget) =>
        updateBudgetCell({
          row,
          value,
          month: currentMonth
        })
    };
  });

  return [
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: EXPENSE_TYPE_NAME,
      filterable: false,
      minWidth: 200,
      maxWidth: 400,
      renderHeader: () => <p className="bold">{translate('labels.name')}</p>
    },
    ...months,
    {
      ...COLUMNS_DEFAULT_OPTIONS,
      field: 'total',
      filterable: false,
      minWidth: 100,
      maxWidth: 150,
      align: 'right',
      headerAlign: 'right',
      weight: 5,
      renderHeader: () => <p className="bold">{translate('labels.total')}</p>,
      valueGetter: (_value: unknown, row: ExpenseTypeBudget) =>
        row.budgets.reduce((acc, curr) => acc + (curr.amount || 0), 0),
      renderCell: ({ value }) => (
        <p className="bold">{formatNumberWithSpaces(value)}</p>
      )
    }
  ] as GridColDef[];
};
